"use client"

import HomeDetailsWrapperAccordion from "@/features/homeowners/components/home-details-wrapper-accordion"
import { kindOfRoofWorkOptions, workScopeStatusOptions, roofPitchOptions } from "../../constants/options"
import { HomeWithHomeOwnerFormType } from "../../schema/home-with-home-owner.schema"
import { useFormContext, useWatch } from "react-hook-form"
import FormSelectInput from "../../../../shared/components/form-inputs/form-select-input"
import WorkScopeStatusSelect from "../work-scope-status-select"
import FormMultiSelectInput from "../../../../shared/components/form-inputs/form-multi-select-input"
import FormTextareaInput from "../../../../shared/components/form-inputs/form-textarea-input"
import DateStampedTextarea from "../../../../shared/components/form-inputs/form-date-stamped-textarea-input"
import { useClaimedByOptions } from "../../hooks/use-claimed-by-options"
import { useWorkscopeClaimedBySync } from "../../hooks/use-workscope-claimed-by-sync"
import { GoodwillToggleLabel } from "../goodwill-toggle-label"
import { useWorkscopeLabelStyle, WORKSCOPE_LABEL_COLORS } from "../home-details-wrapper-accordion"

const BASE_FIELD_NAME = "home.roofingInfo"

const goodwillGreenStyle = { color: WORKSCOPE_LABEL_COLORS.goodwill } as const

function HomeRoofingForm() {
    const { control, setValue, getValues } = useFormContext<HomeWithHomeOwnerFormType>()
    const { options: claimedByOptions, loading: claimedByLoading } = useClaimedByOptions()
    const goodwillLabelStyle = useWorkscopeLabelStyle(true) ?? goodwillGreenStyle
    const goodwillRoofingOn = useWatch({ control, name: `${BASE_FIELD_NAME}.goodwillRoofingOn` })
    const goodwillRoofTarpOn = useWatch({ control, name: `${BASE_FIELD_NAME}.goodwillRoofTarpOn` })
    
    // Watch status fields and claimed by fields
    const roofing = useWatch({ control, name: `${BASE_FIELD_NAME}.roofing` })
    const roofingClaimedBy = useWatch({ control, name: `${BASE_FIELD_NAME}.roofingClaimedBy` })
    const roofTarpNeeded = useWatch({ control, name: `${BASE_FIELD_NAME}.roofTarpNeeded` })
    const roofTarpClaimedBy = useWatch({ control, name: `${BASE_FIELD_NAME}.roofTarpClaimedBy` })
    const goodwillRoofing = useWatch({ control, name: `${BASE_FIELD_NAME}.goodwillRoofing` })
    const goodwillRoofingClaimedBy = useWatch({ control, name: `${BASE_FIELD_NAME}.goodwillRoofingClaimedBy` })
    const goodwillRoofTarpNeeded = useWatch({ control, name: `${BASE_FIELD_NAME}.goodwillRoofTarpNeeded` })
    const goodwillRoofTarpNeededClaimedBy = useWatch({
        control,
        name: `${BASE_FIELD_NAME}.goodwillRoofTarpNeededClaimedBy`,
    })

    useWorkscopeClaimedBySync<HomeWithHomeOwnerFormType>({
        status: roofing,
        claimedBy: roofingClaimedBy,
        claimedByFieldPath: `${BASE_FIELD_NAME}.roofingClaimedBy`,
        setValue,
        getValues,
    })
    useWorkscopeClaimedBySync<HomeWithHomeOwnerFormType>({
        status: roofTarpNeeded,
        claimedBy: roofTarpClaimedBy,
        claimedByFieldPath: `${BASE_FIELD_NAME}.roofTarpClaimedBy`,
        setValue,
        getValues,
    })
    useWorkscopeClaimedBySync<HomeWithHomeOwnerFormType>({
        status: goodwillRoofing,
        claimedBy: goodwillRoofingClaimedBy,
        claimedByFieldPath: `${BASE_FIELD_NAME}.goodwillRoofingClaimedBy`,
        setValue,
        getValues,
        enabled: !!goodwillRoofingOn,
    })
    useWorkscopeClaimedBySync<HomeWithHomeOwnerFormType>({
        status: goodwillRoofTarpNeeded,
        claimedBy: goodwillRoofTarpNeededClaimedBy,
        claimedByFieldPath: `${BASE_FIELD_NAME}.goodwillRoofTarpNeededClaimedBy`,
        setValue,
        getValues,
        enabled: !!goodwillRoofTarpOn,
    })
    return (
        <HomeDetailsWrapperAccordion heading="Roofing" wrapperClassName="two-cols-grid">
            {/* Row 1: Status | Claimed By (aligned) */}
            <WorkScopeStatusSelect workScopeSlug="install-roofing"
                inputGroupProps={{
                    label: <GoodwillToggleLabel title="Roofing" toggleName={`${BASE_FIELD_NAME}.goodwillRoofingOn` as any} control={control} />,
                }}
                options={workScopeStatusOptions}
                name={`${BASE_FIELD_NAME}.roofing`}
                control={control}
            />
            <FormMultiSelectInput
                inputGroupProps={{ label: "Roofing Claimed By" }}
                options={claimedByOptions}
                name={`${BASE_FIELD_NAME}.roofingClaimedBy`}
                control={control}
                placeholder={claimedByLoading ? "Loading options..." : "Select"}
                searchPlaceholder="Search options..."
            />
            {goodwillRoofingOn && (
                <>
                    <FormSelectInput
                        inputGroupProps={{ label: "Roofing Goodwill", labelStyle: goodwillLabelStyle }}
                        options={workScopeStatusOptions}
                        name={`${BASE_FIELD_NAME}.goodwillRoofing`}
                        control={control}
                    />
                    <FormMultiSelectInput
                        inputGroupProps={{ label: "Roofing Goodwill Claimed By" }}
                        options={claimedByOptions}
                        name={`${BASE_FIELD_NAME}.goodwillRoofingClaimedBy`}
                        control={control}
                        placeholder={claimedByLoading ? "Loading options..." : "Select"}
                        searchPlaceholder="Search options..."
                    />
                </>
            )}
            {/* Row 2: What kind of roof repair | Roof pitch */}
            <FormSelectInput
                inputGroupProps={{ label: "What kind of roof repair?" }}
                options={kindOfRoofWorkOptions}
                name={`${BASE_FIELD_NAME}.kindOfRoofWork`}
                control={control}
            />
            <FormSelectInput
                inputGroupProps={{ label: "What is the pitch of the roof?" }}
                options={roofPitchOptions}
                name={`${BASE_FIELD_NAME}.roofPitch`}
                control={control}
                placeholder="Select"
            />
            {/* Row 3: Roof Tarp Needed | Claimed By */}
            <WorkScopeStatusSelect workScopeSlug="tarp-roof"
                inputGroupProps={{
                    label: <GoodwillToggleLabel title="Roof Tarp Needed" toggleName={`${BASE_FIELD_NAME}.goodwillRoofTarpOn` as any} control={control} />,
                }}
                options={workScopeStatusOptions}
                name={`${BASE_FIELD_NAME}.roofTarpNeeded`}
                control={control}
            />
            <FormMultiSelectInput
                inputGroupProps={{ label: "Roof Tarp Claimed By" }}
                options={claimedByOptions}
                name={`${BASE_FIELD_NAME}.roofTarpClaimedBy`}
                control={control}
                placeholder={claimedByLoading ? "Loading options..." : "Select"}
                searchPlaceholder="Search options..."
            />
            {goodwillRoofTarpOn && (
                <>
                    <FormSelectInput
                        inputGroupProps={{ label: "Roof Tarp Goodwill", labelStyle: goodwillLabelStyle }}
                        options={workScopeStatusOptions}
                        name={`${BASE_FIELD_NAME}.goodwillRoofTarpNeeded`}
                        control={control}
                    />
                    <FormMultiSelectInput
                        inputGroupProps={{ label: "Roof Tarp Goodwill Claimed By" }}
                        options={claimedByOptions}
                        name={`${BASE_FIELD_NAME}.goodwillRoofTarpNeededClaimedBy`}
                        control={control}
                        placeholder={claimedByLoading ? "Loading options..." : "Select"}
                        searchPlaceholder="Search options..."
                    />
                </>
            )}
            <DateStampedTextarea
                inputGroupProps={{
                    label: "Description",
                    wrapperClassName: "two-cols",
                }}
                rows={3}
                placeholder="Describe the roofing work needed"
                name={`${BASE_FIELD_NAME}.description`}
                control={control}
                includeUserName
            />
        </HomeDetailsWrapperAccordion>
    )
}

export default HomeRoofingForm
